import { Injectable, signal, computed, inject, InjectionToken } from '@angular/core';
import type { Ubicacion, DeviceInfo, FingerprintId } from '@core/remito.model';
import { CheckInLocationSyncService } from './checkin-location-sync';
import type { PendingLocation } from './checkin-location-db';
import { ENV_CONFIG } from '@core/config/env.token';

/**
 * Token de inyección para la configuración de geolocalización
 */
export const GEOLOCATION_CONFIG = new InjectionToken<GeolocationConfig>('GEOLOCATION_CONFIG', {
  providedIn: 'root',
  factory: () => ({
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
  }),
});

export interface GeolocationConfig {
  enableHighAccuracy: boolean;
  timeout: number;
  maximumAge: number;
}

export type GeolocationErrorCode =
  | 'PERMISSION_DENIED'
  | 'POSITION_UNAVAILABLE'
  | 'TIMEOUT'
  | 'NOT_SUPPORTED';

export interface GeolocationError {
  code: GeolocationErrorCode;
  message: string;
  originalError?: GeolocationPositionError;
}

/**
 * Servicio para gestión de geolocalización del dispositivo.
 *
 * Proporciona métodos para:
 * - Verificar disponibilidad y permisos de geolocalización
 * - Obtener coordenadas GPS del dispositivo
 * - Monitorear el estado de permisos
 * - Gestionar errores de geolocalización
 *
 * @example
 * ```typescript
 * const service = inject(GeolocationService);
 * const ubicacion = await service.obtenerUbicacion();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class GeolocationService {
  private readonly config = inject(GEOLOCATION_CONFIG);
  private readonly checkInSync = inject(CheckInLocationSyncService);
  private readonly envConfig = inject(ENV_CONFIG);

  readonly permisoGps = signal<"granted" | "denied" | "prompt" | "unknown">("unknown");
  readonly obteniendo = signal(false);
  readonly error = signal<GeolocationError | null>(null);
  private readonly lastKnownLocation = signal<Ubicacion | null>(null);
  private readonly lastKnownAt = signal<number | null>(null);
  private watchId: number | null = null;
  readonly hasRecentLocation = computed(() => this.getCachedLocation(5 * 60 * 1000) !== null);
  private readonly shouldBypassPermissions =
    !this.envConfig.production && this.envConfig.devAutoGrantPermissions === true;

  /**
   * Computed signal que indica si la geolocalización está disponible y permitida
   */
  readonly gpsDisponible = computed(() => this.permisoGps() === 'granted');

  /**
   * Computed signal que indica si la geolocalización está soportada por el navegador
   */
  readonly geolocalizacionSoportada = computed(() => this.verificarSoporte());

  /**
   * Verifica si la API de geolocalización está disponible en el navegador
   * @private
   */
  private verificarSoporte(): boolean {
    if (this.shouldBypassPermissions) {
      return true;
    }
    return 'geolocation' in navigator && 'permissions' in navigator;
  }

  private getDevLocation(): Ubicacion {
    return {
      latitud: -34.6037,
      longitud: -58.3816,
      precision: 25,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Verifica el estado del permiso de geolocalización
   *
   * Actualiza el signal `permisoGps` con el estado actual del permiso.
   *
   * @returns Promise que resuelve a `true` si el permiso está concedido
   * @throws No lanza errores, retorna `false` en caso de fallo
   */
  async verificarPermiso(): Promise<boolean> {
    if (this.shouldBypassPermissions) {
      this.permisoGps.set('granted');
      return true;
    }

    if (!this.verificarSoporte()) {
      this.error.set({
        code: 'NOT_SUPPORTED',
        message: 'Geolocalización no soportada por el navegador',
      });
      return false;
    }

    try {
      const resultado = await navigator.permissions.query({ name: 'geolocation' });
      if (resultado.state === 'prompt' && this.permisoGps() === 'granted') {
        this.permisoGps.set('granted');
      } else {
        this.permisoGps.set(resultado.state);
      }

      // Escuchar cambios en el permiso
      resultado.addEventListener('change', () => {
        this.permisoGps.set(resultado.state);
      });

      return resultado.state === 'granted';
    } catch {
      // Algunos navegadores no soportan la API de permisos
      // En ese caso, intentamos obtener la ubicación directamente
      return false;
    }
  }

  /**
   * Obtiene la ubicación actual del dispositivo
   *
   * Actualiza los signals `obteniendo` y `error` durante el proceso.
   * Utiliza la configuración inyectada para opciones de precisión y timeout.
   *
   * @returns Promise con las coordenadas del dispositivo o null si falla
   * @throws GeolocationError con código específico del tipo de error
   */
  async obtenerUbicacion(): Promise<Ubicacion> {
    if (this.shouldBypassPermissions) {
      const cached = this.getCachedLocation();
      if (cached) {
        this.permisoGps.set('granted');
        return cached;
      }

      const devLocation = this.getDevLocation();
      this.setLastKnownLocation(devLocation);
      this.permisoGps.set('granted');
      return devLocation;
    }

    if (!this.verificarSoporte()) {
      const error: GeolocationError = {
        code: 'NOT_SUPPORTED',
        message: 'Geolocalización no disponible en este navegador',
      };
      this.error.set(error);
      throw error;
    }

    const cached = this.getCachedLocation();
    if (cached) {
      return cached;
    }

    this.obteniendo.set(true);
    this.error.set(null);

    try {
      const position = await this.getCurrentPosition();
      const mapped = this.mapearPosicion(position);
      this.setLastKnownLocation(mapped);
      this.startLocationWatch();
      return mapped;
    } catch (error) {
      const geoError = this.mapearError(error as GeolocationPositionError);
      this.error.set(geoError);
      throw geoError;
    } finally {
      this.obteniendo.set(false);
    }
  }

  /**
   * Obtiene la posición usando la API de geolocalización
   * @private
   */
  private getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        {
          enableHighAccuracy: this.config.enableHighAccuracy,
          timeout: this.config.timeout,
          maximumAge: this.config.maximumAge,
        }
      );
    });
  }

  /**
   * Mapea GeolocationPosition a la interfaz Ubicacion
   * @private
   */
  private mapearPosicion(position: GeolocationPosition): Ubicacion {
    return {
      latitud: position.coords.latitude,
      longitud: position.coords.longitude,
      precision: position.coords.accuracy,
      timestamp: new Date(position.timestamp).toISOString(),
    };
  }

  private setLastKnownLocation(location: Ubicacion): void {
    this.lastKnownLocation.set(location);
    const timestamp = Date.parse(location.timestamp);
    this.lastKnownAt.set(Number.isFinite(timestamp) ? timestamp : Date.now());
  }

  getCachedLocation(maxAgeMs = 2 * 60 * 1000): Ubicacion | null {
    const location = this.lastKnownLocation();
    if (!location) return null;
    const ts = this.lastKnownAt() ?? Date.parse(location.timestamp);
    if (!Number.isFinite(ts)) return null;
    if (Date.now() - ts > maxAgeMs) return null;
    return location;
  }

  async waitForLocation(maxWaitMs = 15000): Promise<Ubicacion | null> {
    const cached = this.getCachedLocation();
    if (cached) return cached;

    this.startLocationWatch();

    return await new Promise(resolve => {
      const startedAt = Date.now();
      const intervalId = window.setInterval(() => {
        const current = this.getCachedLocation();
        if (current) {
          clearInterval(intervalId);
          resolve(current);
          return;
        }
        if (Date.now() - startedAt >= maxWaitMs) {
          clearInterval(intervalId);
          resolve(null);
        }
      }, 250);
    });
  }

  private startLocationWatch(): void {
    if (this.watchId !== null) return;
    if (!('geolocation' in navigator)) return;

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        const mapped = this.mapearPosicion(position);
        this.setLastKnownLocation(mapped);
        this.permisoGps.set('granted');
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          this.permisoGps.set('denied');
          if (this.watchId !== null) {
            navigator.geolocation.clearWatch(this.watchId);
            this.watchId = null;
          }
        }
      },
      {
        enableHighAccuracy: false,
        maximumAge: 600000
      }
    );
  }

  /**
   * Mapea errores de geolocalización a un formato unificado
   * @private
   */
  private mapearError(error: GeolocationPositionError): GeolocationError {
    let code: GeolocationErrorCode;
    let message: string;

    switch (error.code) {
      case error.PERMISSION_DENIED:
        code = 'PERMISSION_DENIED';
        message = 'Permiso de geolocalización denegado. Por favor, habilite el acceso a su ubicación.';
        break;
      case error.POSITION_UNAVAILABLE:
        code = 'POSITION_UNAVAILABLE';
        message = 'Ubicación no disponible. Verifique que el GPS esté activado.';
        break;
      case error.TIMEOUT:
        code = 'TIMEOUT';
        message = `No se pudo obtener la ubicación en ${this.config.timeout / 1000} segundos.`;
        break;
      default:
        code = 'POSITION_UNAVAILABLE';
        message = 'Error desconocido al obtener la ubicación.';
    }

    return { code, message, originalError: error };
  }

  /**
   * Limpia el estado de error
   */
  limpiarError(): void {
    this.error.set(null);
  }

  /**
   * Persiste la ubicación capturada como parte del proceso de Check-in.
   *
   * @param ubicacion - Datos de GPS obtenidos del dispositivo
   * @param deviceInfo - Opcional: Información del dispositivo y contexto
   * @param fingerprintId - Opcional: ID de fingerprint para identidad adicional
   * @returns Registro almacenado en IndexedDB
   */
  async registrarCheckInLocalizacion(
    ubicacion: Ubicacion | { ubicacion: Ubicacion; deviceInfo?: DeviceInfo },
    deviceInfo?: DeviceInfo,
    fingerprintId?: FingerprintId
  ): Promise<PendingLocation> {
    const normalizedLocation = 'latitud' in ubicacion ? ubicacion : ubicacion.ubicacion;
    const normalizedDeviceInfo = 'latitud' in ubicacion ? deviceInfo : (deviceInfo ?? ubicacion.deviceInfo);
    return this.checkInSync.registrar(normalizedLocation, fingerprintId, normalizedDeviceInfo);
  }

  async limpiarCheckInGuardado(): Promise<void> {
    await this.checkInSync.clearStoredCheckIns();
  }

  async obtenerCheckInPersistido(): Promise<PendingLocation | null> {
    return this.checkInSync.getStoredCheckIn();
  }

  /**
   * Solicita el prompt de permiso de geolocalización (prompt-only)
   *
   * Este método NO obtiene coordenadas reales, solo dispara el prompt del navegador.
   * Se usa en Home para verificar permisos sin necesidad de coordenadas.
   *
   * @returns Promise que resuelve a `true` si el permiso fue concedido
   */
  async ensurePermissionPrompt(): Promise<boolean> {
    if (this.shouldBypassPermissions) {
      this.permisoGps.set('granted');
      if (!this.getCachedLocation()) {
        this.setLastKnownLocation(this.getDevLocation());
      }
      return true;
    }

    try {
      if (!('geolocation' in navigator)) {
        this.permisoGps.set('unknown');
        return false;
      }

      let status: PermissionStatus | null = null;
      const positionTimeoutMs = 15000;
      const pollIntervalMs = 250;

      const queryPermission = async (): Promise<PermissionState | null> => {
        try {
          if ('permissions' in navigator) {
            const result = await navigator.permissions.query({ name: 'geolocation' });
            return result.state;
          }
        } catch {
          return null;
        }
        return null;
      };

      try {
        if ('permissions' in navigator) {
          status = await navigator.permissions.query({ name: 'geolocation' });
          this.permisoGps.set(status.state);
          if (status.state === 'granted') {
            this.startLocationWatch();
            return true;
          }
          if (status.state === 'denied') return false;
        }
      } catch {
        status = null;
      }

      // Solicitar ubicación para forzar el prompt y esperar a que el usuario decida
      return await new Promise<boolean>((resolve) => {
        let settled = false;
        let pollId: number | null = null;
        const cleanup = () => {
          if (status) status.removeEventListener('change', onChange);
          if (pollId != null) clearInterval(pollId);
        };

        const finalize = (granted: boolean, state?: PermissionState) => {
          if (settled) return;
          settled = true;
          if (state) {
            this.permisoGps.set(state);
          } else {
            this.permisoGps.set(granted ? 'granted' : 'denied');
          }
          cleanup();
          resolve(granted);
        };

        const onChange = () => {
          if (!status) return;
          if (status.state === 'granted' || status.state === 'denied') {
            finalize(status.state === 'granted', status.state);
          }
        };

        if (status) {
          status.addEventListener('change', onChange);
          pollId = window.setInterval(async () => {
            if (status && (status.state === 'granted' || status.state === 'denied')) {
              finalize(status.state === 'granted', status.state);
              return;
            }
            const latestState = await queryPermission();
            if (latestState === 'granted' || latestState === 'denied') {
              finalize(latestState === 'granted', latestState);
            }
          }, pollIntervalMs);
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = this.mapearPosicion(position);
            this.setLastKnownLocation(location);
            this.startLocationWatch();
            finalize(true, 'granted');
          },
          async (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              finalize(false, 'denied');
              return;
            }

            if (status?.state === 'denied') {
              finalize(false, 'denied');
              return;
            }

            const latestState = await queryPermission();
            if (latestState === 'denied') {
              finalize(false, 'denied');
              return;
            }

            // Si el error no es por permisos (timeout/position unavailable), asumimos permiso concedido.
            this.startLocationWatch();
            finalize(true, 'granted');
          },
          {
            enableHighAccuracy: false,
            maximumAge: 600000,
            timeout: positionTimeoutMs
          }
        );
      });
    } catch {
      this.permisoGps.set('unknown');
      return false;
    }
  }
}
